import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { MESSAGES } from '@guuru/chat-common';
import { ChatAPI, useChatSettings } from '@guuru/chat-web';
import ChatAvatar from './ChatAvatar';
import ChatRenderAttachment from './renders/Attachment';
import ChatRenderButton from './renders/Button';
import ChatRenderCategories from './renders/Categories';
import ChatRenderContact from './renders/Contact';
import Delete from './renders/Delete';
import ChatRenderEmoji from './renders/Emoji';
import ChatRenderIntentSelection from './renders/IntentSelection';
import ChatRenderLinkPreview from './renders/LinkPreview';
import ChatRenderPartners from './renders/Partners';
import ChatRenderPredictionAnswer from './renders/PredictionAnswer';
import ChatRenderPredictionConfirmation from './renders/PredictionConfirmation';
import ChatRenderQuickActions from './renders/QuickActions';
import ChatRenderText from './renders/Text';
import ChatRenderUserInfo from './renders/UserInfo';
import ChatRenderVisitorRole from './renders/VisitorRole';
import WaitFallback from './renders/WaitFallback';
import ChatRenderWaitTimeout from './renders/WaitTimeout';
import MessageWrapper from './style';

const getFullWidth = (messageType, isBot, side, smartFormStyle) => {
  const isLeftSide = side === 'left';
  return isBot && isLeftSide && !smartFormStyle ? 'mod--fullWidth' : '';
};

const ChatMessage = function ({
  chatId = null,
  message,
  side,
  showProfileImage,
  showBotAvatar,
  customBotAvatar = null,
  readOnly = false,
  preview = false,
  partner = null,
  showDelete = false,
  onRunNextStep = () => {},
  onSubmitChatText = () => {},
  locale = null,
}) {
  const chatLocale = locale || ChatAPI.storeRetrieveLocale();
  const {
    smartFormStyle,
    chatAlignment,
    author: { __typename, photoUrl } = {},
  } = message || {};

  const { data: { chatSettings: { chatWidgetShowProfiles } = {} } = {} } =
    useChatSettings();

  const isExpert = __typename === 'ExpertAuthor';
  const isBot = __typename === 'BotAuthor';
  const isHeroesFormat =
    chatWidgetShowProfiles && message?.type === MESSAGES.welcome && !readOnly;

  const chatWrapperClassName = useMemo(() => {
    const showAvatar = !showProfileImage && !smartFormStyle;
    const skipProfileImage = showAvatar ? 'skip-profile-image' : '';
    const withoutProfileImage =
      (isExpert || (isBot && showBotAvatar)) && showAvatar
        ? 'without-profile-image'
        : '';
    const fullWidth = getFullWidth(message.type, isBot, side, smartFormStyle);
    const smartForm = smartFormStyle ? 'smart-form' : '';
    const bot = isBot ? 'bot' : '';
    return [
      'chat-message-wrapper',
      `mod--${side}`,
      fullWidth,
      skipProfileImage,
      withoutProfileImage,
      smartForm,
      bot,
    ]
      .filter(Boolean)
      .join(' ');
  }, [
    isBot,
    isExpert,
    message.type,
    showProfileImage,
    showBotAvatar,
    side,
    smartFormStyle,
  ]);

  const renderedMessage = useMemo(() => {
    switch (message.type) {
      case MESSAGES.category:
        return (
          <ChatRenderCategories
            message={message}
            locale={chatLocale}
            readOnly={readOnly}
            onRunNextStep={onRunNextStep}
          />
        );
      case MESSAGES.visitorRole:
        return (
          <ChatRenderVisitorRole
            message={message}
            onRunNextStep={onRunNextStep}
          />
        );
      case MESSAGES.partnerSelection:
        return <ChatRenderPartners message={message} />;
      case MESSAGES.quickActionSelection:
        return (
          <ChatRenderQuickActions
            message={message}
            onRunNextStep={onRunNextStep}
          />
        );
      case MESSAGES.button:
        return (
          <ChatRenderButton
            chatId={chatId}
            message={message}
            onSubmitChatText={onSubmitChatText}
            readOnly={readOnly}
          />
        );
      case MESSAGES.smsFallback:
      case MESSAGES.waitFallback:
        return (
          <WaitFallback message={message} readOnly={readOnly} chatId={chatId} />
        );
      case MESSAGES.predictionAnswer:
        return <ChatRenderPredictionAnswer message={message} />;
      case MESSAGES.predictionResult:
        return (
          <ChatRenderPredictionConfirmation
            message={message}
            readOnly={readOnly}
            preview={preview}
            onRunNextStep={onRunNextStep}
          />
        );
      case MESSAGES.userInfo:
        return (
          <ChatRenderUserInfo
            chatId={chatId}
            message={message}
            readOnly={readOnly}
          />
        );
      case MESSAGES.waitTimeout:
        return (
          <ChatRenderWaitTimeout
            message={message}
            chatId={chatId}
            readOnly={readOnly}
          />
        );
      case MESSAGES.contact:
        return (
          <ChatRenderContact
            message={message}
            readOnly={readOnly}
            chatId={chatId}
          />
        );
      case MESSAGES.attachment:
        return <ChatRenderAttachment message={message} />;
      case 'link':
        return <ChatRenderLinkPreview message={message} />;
      case MESSAGES.emoji:
        return <ChatRenderEmoji message={message} />;
      case MESSAGES.intentSelection:
        return (
          <ChatRenderIntentSelection
            message={message}
            readOnly={readOnly}
            partnerId={partner?.id}
            onRunNextStep={onRunNextStep}
            locale={chatLocale}
          />
        );
      default:
        return <ChatRenderText message={message} tooltip={!smartFormStyle} />;
    }
  }, [
    message,
    chatLocale,
    readOnly,
    chatId,
    preview,
    partner,
    smartFormStyle,
    onRunNextStep,
    onSubmitChatText,
  ]);

  if (!renderedMessage) return null;

  return (
    <div className="row">
      <div className={smartFormStyle ? null : `col message-${side}`}>
        <MessageWrapper
          className={chatWrapperClassName}
          chatAlignment={chatAlignment}
        >
          <ChatAvatar
            url={photoUrl}
            isBot={isBot}
            isExpert={isExpert}
            showProfileImage={showProfileImage}
            customBotAvatar={customBotAvatar}
            isHeroesFormat={isHeroesFormat}
            isHidden={
              smartFormStyle ||
              (isBot && !showBotAvatar) ||
              [MESSAGES.quickActionSelection].includes(message.type)
            }
          />
          {renderedMessage}
          {showDelete && <Delete messageId={message.id} chatId={chatId} />}
        </MessageWrapper>
      </div>
    </div>
  );
};

ChatMessage.propTypes = {
  chatId: PropTypes.string,
  message: PropTypes.shape({
    id: PropTypes.string,
    author: PropTypes.shape({
      id: PropTypes.string,
    }),
    type: PropTypes.string,
    __typename: PropTypes.string,
    smartFormStyle: PropTypes.bool,
    chatAlignment: PropTypes.string,
  }).isRequired,
  side: PropTypes.string.isRequired,
  showProfileImage: PropTypes.bool.isRequired,
  showBotAvatar: PropTypes.bool.isRequired,
  customBotAvatar: PropTypes.string,
  readOnly: PropTypes.bool,
  preview: PropTypes.bool,
  partner: PropTypes.shape({
    id: PropTypes.string,
  }),
  showDelete: PropTypes.bool,
  onRunNextStep: PropTypes.func,
  onSubmitChatText: PropTypes.func,
  locale: PropTypes.string,
};

export default React.memo(
  ChatMessage,
  (prev, next) =>
    prev.message.id === next.message.id &&
    prev.showProfileImage === next.showProfileImage &&
    prev.message.text === next.message.text,
);
